import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button, Modal, Skeleton } from '../admin/ui';
import { useToast } from '../admin/overlays';
import { IconCard, IconCheck } from '../icons';
import { SELF_SERVE_PLAN_KEYS, SHOW_PAY_PER_CASE, billingLabel, planAccent, planLabel } from '../plans';

/**
 * The "continue as you already are" option, for the two block reasons that
 * have one — mirrors ActivationModal's CONTINUE_CONFIG on the magic-link
 * case page. Only the endpoint and copy differ; `continueKind` picks which.
 */
const CONTINUE_CONFIG = {
  payPerCase: {
    endpoint: (caseId) => `/api/portal/cases/${caseId}/pay`,
    label: 'Continue with Pay per case',
    subtitle: 'Pay for this case and keep going exactly as you have been.'
  },
  quotaFilled: {
    endpoint: (caseId) => `/api/portal/cases/${caseId}/pay-overage`,
    label: 'Pay for this case',
    subtitle: "Settle this one case at your plan's overage rate, without changing plans."
  }
};

/**
 * The plan cards themselves, with no page or modal chrome — used both inline
 * on the Account page and inside UpgradeModal below, so the upgrade/downgrade
 * logic exists in exactly one place rather than two copies that can drift.
 *
 * Checks out via the portal-authenticated /api/portal/plan-checkout, not the
 * public /api/stripe/plan-checkout the anonymous signup form uses — that one
 * refuses an email that already has an account (it exists specifically to
 * stop the signup form being reused as a way to hijack an existing one), and
 * every merchant reaching this grid already has one. Both share the same
 * plan_signup webhook branch underneath, which finds-or-updates a merchant
 * by email either way.
 *
 * `caseId`/`continueKind`/`continueAmount` are only passed when this opened
 * because a specific case needs payment (an AWAITING_REVIEW or QUOTA_FILLED
 * block) — they add a "continue as you are" option ahead of the plan grid,
 * mirroring the magic-link case page's ActivationModal rather than just
 * re-offering a bare plan chooser to someone who already picked a plan.
 */
export const PlanUpgradeGrid = ({ merchant, onCheckoutStarted, caseId, continueKind, continueAmount }) => {
  const toast = useToast();
  const [pricing, setPricing] = useState(null);
  const [startingPlan, setStartingPlan] = useState(null);
  const [payingCase, setPayingCase] = useState(false);
  // See SHOW_PAY_PER_CASE: the "keep paying per case" option is not offered
  // any more, so a merchant on it gets the plan grid alone. quotaFilled's
  // "pay for this case" stays — that is plan overage, not pay-per-case.
  const continueConfig =
    caseId && (continueKind !== 'payPerCase' || SHOW_PAY_PER_CASE)
      ? CONTINUE_CONFIG[continueKind]
      : null;

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/merchants/pricing')
      .then(({ data }) => {
        if (!cancelled) setPricing(data);
      })
      .catch(() => {
        if (!cancelled) toast('Could not load plan pricing.', { tone: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = async () => {
    if (!continueConfig) return;
    setPayingCase(true);
    try {
      const { data } = await api.post(continueConfig.endpoint(caseId));
      if (data?.url) {
        onCheckoutStarted?.();
        window.location.href = data.url;
        return;
      }
      toast('Could not start checkout. Please try again.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not start checkout. Please try again.', {
        tone: 'error'
      });
    } finally {
      setPayingCase(false);
    }
  };

  const startCheckout = async (planKey) => {
    setStartingPlan(planKey);
    try {
      // Portal-authenticated endpoint, not the public /api/stripe/plan-checkout
      // the anonymous signup form uses — that one deliberately refuses an
      // email that already has an account, which is exactly what every
      // merchant using this grid already has.
      const { data } = await api.post('/api/portal/plan-checkout', { plan: planKey });
      if (data?.url) {
        onCheckoutStarted?.();
        window.location.href = data.url;
        return;
      }
      // No URL but success: an existing subscription was switched in place
      // rather than sent to Stripe Checkout for a brand-new one — nothing to
      // redirect to. Reload so every page reading merchant/usage state (this
      // grid is shown from three different ones) reflects the new plan,
      // the same way returning from a real Stripe redirect would.
      if (data?.success) {
        onCheckoutStarted?.();
        toast('Plan updated.', { tone: 'success' });
        window.location.reload();
        return;
      }
      toast('Could not start checkout. Please try again.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not start checkout. Please try again.', {
        tone: 'error'
      });
    } finally {
      setStartingPlan(null);
    }
  };

  const plans = pricing?.merchantPlanPrices || {};
  // billingModel stays 'subscription' after a cancellation, so on its own it
  // would mark the plan they just lost as "Current plan" — leaving a
  // returning merchant a grid of cards with nothing to click. Stripe's
  // status is what says whether the subscription is still live.
  const onLivePlan =
    merchant.billingModel === 'subscription' && merchant.subscription?.status !== 'canceled';
  // SELF_SERVE_PLAN_KEYS is already in ascending order (matches the canonical
  // plan ladder used for display everywhere else), so array position doubles
  // as price rank. A merchant not currently on a subscription (pilot,
  // pay-per-case, nothing yet) has no rank in this ladder at all — every
  // plan reads as an upgrade for them, which is the correct framing.
  const currentRank = onLivePlan ? SELF_SERVE_PLAN_KEYS.indexOf(merchant.plan) : -1;

  if (!pricing) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600">
        {!onLivePlan && merchant.billingModel === 'subscription' ? (
          <>
            Your <span className="font-600 text-ink-900">{billingLabel(merchant)}</span> subscription
            has ended.
          </>
        ) : (
          <>
        You're currently on <span className="font-600 text-ink-900">{billingLabel(merchant)}</span>
        {merchant.billingModel === 'pay_per_case' &&
          typeof merchant.payPerCaseCount === 'number' &&
          ` — ${merchant.payPerCaseCount} case${merchant.payPerCaseCount === 1 ? '' : 's'} paid for so far.`}
        {merchant.billingModel === 'free' && merchant.freeCaseAllowance != null &&
          ` — ${merchant.pilotCasesUsed || 0} of ${merchant.freeCaseAllowance} pilot cases used.`}
          </>
        )}
      </p>

      {continueConfig && (
        <div className="flex flex-col gap-3 rounded-xl border-2 border-gold-500 bg-gold-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold-50 text-gold-700">
              <IconCard size={19} />
            </span>
            <div>
              <p className="font-display text-sm font-700 text-ink-900">
                {continueConfig.label}
                {continueAmount != null && ` — £${Number(continueAmount).toFixed(2)}`}
              </p>
              <p className="mt-1 text-sm text-ink-600">{continueConfig.subtitle}</p>
            </div>
          </div>
          <Button
            size="sm"
            loading={payingCase}
            disabled={payingCase}
            onClick={handleContinue}
            className="shrink-0"
          >
            {payingCase ? 'Redirecting to payment…' : 'Continue'}
          </Button>
        </div>
      )}

      {continueConfig && (
        <p className="text-sm font-600 text-ink-800">
          Or {continueKind === 'quotaFilled' ? 'upgrade to a bigger' : 'switch to a monthly'} plan:
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:max-w-xs">
        {SELF_SERVE_PLAN_KEYS.map((key) => {
          const pl = plans[key];
          if (!pl) return null;
          const isCurrent = onLivePlan && merchant.plan === key;
          const isDowngrade = !isCurrent && currentRank >= 0 && SELF_SERVE_PLAN_KEYS.indexOf(key) < currentRank;
          const accent = planAccent(key);

          return (
            <div
              key={key}
              className={`flex flex-col rounded-xl border-2 p-4 ${
                isCurrent ? 'border-brand-800 bg-brand-50/60' : accent.borderSoft
              }`}
            >
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-md font-display text-xs font-800 ${accent.badgeBg} ${accent.badgeText}`}
              >
                {planLabel(key).charAt(0)}
              </span>
              <p className="mt-2 font-display text-sm font-700 text-ink-900">{planLabel(key)}</p>
              <p className="mt-1.5 font-display text-lg font-800 tracking-tight text-ink-900">
                £{Number(pl.price).toFixed(2)}
                <span className="ml-1 text-xs font-500 text-ink-500">/user/month</span>
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink-600">
                {pl.includedCases != null
                  ? `${pl.includedCases.toLocaleString('en-GB')} cases included`
                  : 'Unlimited cases'}
              </p>
              {Array.isArray(pl.features) && pl.features.length > 0 && (
                <ul className="mt-2.5 space-y-1.5 border-t border-ink-100 pt-2.5">
                  {pl.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-1.5 text-xs leading-snug text-ink-600">
                      <IconCheck size={12} className="mt-0.5 shrink-0 text-brand-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4">
                {isCurrent ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-600 text-brand-700">
                    <IconCheck size={14} />
                    Current plan
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant={isDowngrade ? 'secondary' : 'gold'}
                    className="w-full"
                    loading={startingPlan === key}
                    disabled={startingPlan != null}
                    onClick={() => startCheckout(key)}
                  >
                    {isDowngrade ? 'Downgrade to' : 'Upgrade to'} {planLabel(key)}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * The same grid, reachable from anywhere in the portal without leaving the
 * current page — the dashboard and cases banners open this instead of
 * navigating to Account. Account itself keeps the grid inline, not behind a
 * modal, since it's already the dedicated place for it.
 */
const UPGRADE_MODAL_COPY = {
  payPerCase: {
    title: "You're on Pay per case",
    description: "This case hasn't been paid for yet. Continue paying per case, or switch to a monthly plan below."
  },
  quotaFilled: {
    title: "You've used this cycle's included cases",
    description: "This case hasn't been paid for yet. Pay it at your overage rate, or upgrade to a bigger plan below."
  }
};

export const UpgradeModal = ({ open, onClose, merchant, caseId, continueKind, continueAmount }) => {
  const copy = caseId ? UPGRADE_MODAL_COPY[continueKind] : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={copy?.title || 'Choose a plan'}
      description={
        copy?.description ||
        'Move to a paid plan at any time — your current plan and usage carry over until the new one takes effect.'
      }
      size="lg"
    >
      {merchant && (
        <PlanUpgradeGrid
          merchant={merchant}
          onCheckoutStarted={onClose}
          caseId={caseId}
          continueKind={continueKind}
          continueAmount={continueAmount}
        />
      )}
    </Modal>
  );
};
